export const ArchformShortcutKeyTypes = {
  SetViewFront: "SetViewFront",
  SetViewBack: "SetViewBack",
  SetViewRight: "SetViewRight",
  SetViewLeft: "SetViewLeft",
  SetViewUpper: "SetViewUpper",
  SetViewLower: "SetViewLower",
  ShowHideUpperArch: "ShowHideUpperArch",
  ShowHideLowerArch: "ShowHideLowerArch",
  CenterTooth: "CenterTooth",
  ShowHideGrid: "ShowHideGrid",
  ShowHideIPR: "ShowHideIPR",
  ShowHideCollision: "ShowHideCollision",
  ShowHideSuperimposition: "ShowHideSuperimposition",
  ShowHideGum: "ShowHideGum",
  TimeLinePlay: "TimeLinePlay",
  ToInitialStage: "ToInitialStage",
  ToFinalStage: "ToFinalStage",
  ToPreviousStage: "ToPreviousStage",
  ToNextStage: "ToNextStage",
  NextTooth: "NextTooth",
  PreviousTooth: "PreviousTooth",
  SwitchArch: "SwitchArch",
};

const ARCHFORM_SHORTCUT_COMMANDS = {
  SetViewFront: "setViewFront",
  SetViewBack: "setViewBack",
  SetViewRight: "setViewRight",
  SetViewLeft: "setViewLeft",
  SetViewUpper: "setViewUpper",
  SetViewLower: "setViewLower",
  ShowHideUpperArch: "toggleUpperArchVisibility",
  ShowHideLowerArch: "toggleLowerArchVisibility",
  CenterTooth: "centerTooth",
  ShowHideGrid: "toggleGrid",
  ShowHideIPR: "toggleIPR",
  ShowHideCollision: "toggleCollision",
  ShowHideSuperimposition: "toggleSuperimposition",
  ShowHideGum: "toggleGum",
  TimeLinePlay: "toggleTimelinePlay",
  ToInitialStage: "toInitialStage",
  ToFinalStage: "toFinalStage",
  ToPreviousStage: "toPreviousStage",
  ToNextStage: "toNextStage",
  NextTooth: "nextTooth",
  PreviousTooth: "previousTooth",
  SwitchArch: "switchArch",
};

const isMacOS = () => {
  const nav = typeof navigator !== "undefined" ? navigator : {};
  const platform = nav.userAgentData?.platform || nav.platform || "";
  const userAgent = nav.userAgent || "";
  return /mac/i.test(platform) || /mac os x/i.test(userAgent);
};

const isEditableElement = (target) => {
  if (!target) return false;
  const tagName = target.tagName?.toLowerCase();
  if (tagName === "input" || tagName === "textarea" || tagName === "select") {
    return true;
  }
  return target.isContentEditable;
};

const normalizeKey = (key) => {
  if (!key) return "";
  if (key.length === 1) return key.toLowerCase();
  return key.toLowerCase();
};

const resolveBindingCodes = (key) => {
  const normalized = normalizeKey(key);
  if (!normalized) return [];
  if (/^[a-z]$/.test(normalized)) return [`Key${normalized.toUpperCase()}`];
  if (/^[0-9]$/.test(normalized)) {
    return [`Digit${normalized}`, `Numpad${normalized}`];
  }
  if (normalized === "tab") return ["Tab"];
  if (normalized === "arrowleft") return ["ArrowLeft"];
  if (normalized === "arrowright") return ["ArrowRight"];
  if (normalized === "arrowup") return ["ArrowUp"];
  if (normalized === "arrowdown") return ["ArrowDown"];
  if (normalized === " ") return ["Space"];
  return [];
};

export class ArchformShortcutKeyManager {
  constructor() {
    this._bindings = [];
    this._commands = {};
    this._initializeReleaseShortcutsKey();
  }

  setCommands(commands) {
    this._commands = commands || {};
  }

  registerKey(type, windowsBinding, macBinding, commandName) {
    const normalizeBinding = (binding = {}) => {
      const nextBinding = { ...binding };
      if (!nextBinding.codes && nextBinding.key) {
        nextBinding.codes = resolveBindingCodes(nextBinding.key);
      }
      return nextBinding;
    };
    this._bindings.push({
      type,
      windowsBinding: normalizeBinding(windowsBinding),
      macBinding: normalizeBinding(macBinding),
      commandName,
    });
  }

  handleKeyDown(event) {
    if (isEditableElement(event.target)) {
      return false;
    }

    const currentPlatformIsMac = isMacOS();
    for (let i = 0; i < this._bindings.length; i++) {
      const binding = this._bindings[i];
      const targetBinding = currentPlatformIsMac
        ? binding.macBinding
        : binding.windowsBinding;
      if (this._isMatch(event, targetBinding)) {
        if (event.cancelable) {
          event.preventDefault();
        }
        event.stopPropagation();
        const command = this._commands[binding.commandName];
        if (typeof command === "function") {
          command();
          return true;
        }
      }
    }
    return false;
  }

  _initializeReleaseShortcutsKey() {
    this.registerKey(
      ArchformShortcutKeyTypes.SetViewFront,
      { altKey: true, key: "1" },
      { altKey: true, key: "1" },
      ARCHFORM_SHORTCUT_COMMANDS.SetViewFront
    );
    this.registerKey(
      ArchformShortcutKeyTypes.SetViewBack,
      { altKey: true, key: "2" },
      { altKey: true, key: "2" },
      ARCHFORM_SHORTCUT_COMMANDS.SetViewBack
    );
    this.registerKey(
      ArchformShortcutKeyTypes.SetViewRight,
      { altKey: true, key: "3" },
      { altKey: true, key: "3" },
      ARCHFORM_SHORTCUT_COMMANDS.SetViewRight
    );
    this.registerKey(
      ArchformShortcutKeyTypes.SetViewLeft,
      { altKey: true, key: "4" },
      { altKey: true, key: "4" },
      ARCHFORM_SHORTCUT_COMMANDS.SetViewLeft
    );
    this.registerKey(
      ArchformShortcutKeyTypes.SetViewUpper,
      { altKey: true, key: "q" },
      { altKey: true, key: "q" },
      ARCHFORM_SHORTCUT_COMMANDS.SetViewUpper
    );
    this.registerKey(
      ArchformShortcutKeyTypes.SetViewLower,
      { altKey: true, key: "e" },
      { altKey: true, key: "e" },
      ARCHFORM_SHORTCUT_COMMANDS.SetViewLower
    );
    this.registerKey(
      ArchformShortcutKeyTypes.ShowHideUpperArch,
      { altKey: true, shiftKey: true, key: "q" },
      { altKey: true, shiftKey: true, key: "q" },
      ARCHFORM_SHORTCUT_COMMANDS.ShowHideUpperArch
    );
    this.registerKey(
      ArchformShortcutKeyTypes.ShowHideLowerArch,
      { altKey: true, shiftKey: true, key: "e" },
      { altKey: true, shiftKey: true, key: "e" },
      ARCHFORM_SHORTCUT_COMMANDS.ShowHideLowerArch
    );
    // this.registerKey(
    //   ArchformShortcutKeyTypes.CenterTooth,
    //   { altKey: true, key: "c" },
    //   { altKey: true, key: "c" },
    //   ARCHFORM_SHORTCUT_COMMANDS.CenterTooth
    // );

    this.registerKey(
      ArchformShortcutKeyTypes.ShowHideGrid,
      { altKey: true, key: "g" },
      { altKey: true, key: "g" },
      ARCHFORM_SHORTCUT_COMMANDS.ShowHideGrid
    );
    this.registerKey(
      ArchformShortcutKeyTypes.ShowHideIPR,
      { altKey: true, key: "p" },
      { altKey: true, key: "p" },
      ARCHFORM_SHORTCUT_COMMANDS.ShowHideIPR
    );
    this.registerKey(
      ArchformShortcutKeyTypes.ShowHideCollision,
      { altKey: true, key: "i" },
      { altKey: true, key: "i" },
      ARCHFORM_SHORTCUT_COMMANDS.ShowHideCollision
    );
    this.registerKey(
      ArchformShortcutKeyTypes.ShowHideSuperimposition,
      { altKey: true, key: "o" },
      { altKey: true, key: "o" },
      ARCHFORM_SHORTCUT_COMMANDS.ShowHideSuperimposition
    );
    this.registerKey(
      ArchformShortcutKeyTypes.ShowHideGum,
      { altKey: true, key: "m" },
      { altKey: true, key: "m" },
      ARCHFORM_SHORTCUT_COMMANDS.ShowHideGum
    );

    this.registerKey(
      ArchformShortcutKeyTypes.TimeLinePlay,
      { key: " " },
      { key: " " },
      ARCHFORM_SHORTCUT_COMMANDS.TimeLinePlay
    );
    this.registerKey(
      ArchformShortcutKeyTypes.ToInitialStage,
      { shiftKey: true, key: "arrowleft" },
      { shiftKey: true, key: "arrowleft" },
      ARCHFORM_SHORTCUT_COMMANDS.ToInitialStage
    );
    this.registerKey(
      ArchformShortcutKeyTypes.ToFinalStage,
      { shiftKey: true, key: "arrowright" },
      { shiftKey: true, key: "arrowright" },
      ARCHFORM_SHORTCUT_COMMANDS.ToFinalStage
    );
    this.registerKey(
      ArchformShortcutKeyTypes.ToPreviousStage,
      { key: "arrowleft" },
      { key: "arrowleft" },
      ARCHFORM_SHORTCUT_COMMANDS.ToPreviousStage
    );
    this.registerKey(
      ArchformShortcutKeyTypes.ToNextStage,
      { key: "arrowright" },
      { key: "arrowright" },
      ARCHFORM_SHORTCUT_COMMANDS.ToNextStage
    );

    this.registerKey(
      ArchformShortcutKeyTypes.NextTooth,
      { key: "tab" },
      { key: "tab" },
      ARCHFORM_SHORTCUT_COMMANDS.NextTooth
    );
    this.registerKey(
      ArchformShortcutKeyTypes.PreviousTooth,
      { metaKey: true, ctrlKey: true, key: "tab" },
      { altKey: true, ctrlKey: true, key: "tab" },
      ARCHFORM_SHORTCUT_COMMANDS.PreviousTooth
    );
    this.registerKey(
      ArchformShortcutKeyTypes.SwitchArch,
      { shiftKey: true, key: "tab" },
      { shiftKey: true, key: "tab" },
      ARCHFORM_SHORTCUT_COMMANDS.SwitchArch
    );
  }

  _isMatch(event, targetBinding) {
    const targetCodes = Array.isArray(targetBinding.codes)
      ? targetBinding.codes
      : resolveBindingCodes(targetBinding.key);
    if (targetCodes.length > 0 && !targetCodes.includes(event.code)) {
      return false;
    }

    const key = normalizeKey(event.key);
    const targetKey = normalizeKey(targetBinding.key);
    if (targetCodes.length === 0 && key !== targetKey) {
      return false;
    }

    const expectedAlt = Boolean(targetBinding.altKey);
    const expectedCtrl = Boolean(targetBinding.ctrlKey);
    const expectedShift = Boolean(targetBinding.shiftKey);
    const expectedMeta = Boolean(targetBinding.metaKey);

    if (Boolean(event.altKey) !== expectedAlt) return false;
    if (Boolean(event.ctrlKey) !== expectedCtrl) return false;
    if (Boolean(event.shiftKey) !== expectedShift) return false;
    if (Boolean(event.metaKey) !== expectedMeta) return false;

    return true;
  }
}

